/**
 * THE role picker. Every surface that asks someone to choose a role builds its options here —
 * onboarding's access request, the invitation flows, Grant access, the User Management change-role
 * dialogs — so the same catalogue cannot render four different ways.
 *
 * It was written for the Grant access wizard and copied nowhere, which is why the other pickers
 * showed bare alphabet soup: no descriptions, Owner tiers scattered among the disciplines. A role id
 * is not self-explanatory ("Game Publisher" vs "Game Client Owner" is a real decision), so the
 * description is part of the option rather than a nicety.
 *
 * Order is deterministic and self-maintaining: system governance tiers first (Owner → Co-Owner → any
 * other system role A–Z), then org/team custom roles alphabetically. A new team role slots into the
 * alphabetical group on its own — nothing here to curate as the catalogue grows.
 */

// Header rows are inert options. `isDisabled` is the prop Select honours — it is what stops both the
// click and the keyboard landing on a header; `className` only makes it LOOK like a heading. The
// wizard used to pass `disabled` plus a class the Select ignored entirely, so "Access tiers" was a
// selectable value that set the role to "__hdr_tiers".
const GROUP_CLASS = 'select-option-group';

// A picker line has to fit beside its neighbours, not explain the role in full.
const MAX_SUMMARY = 96;

/**
 * Disciplines a requester may ask for on a game — registry shape, so they run through
 * buildRoleOptions like every other picker's roles.
 *
 * Non-escalating job functions only. The org-wide executive tiers (CEO / GM / GGM) and the
 * *_OWNER / *_CO_OWNER tiers are intentionally excluded: those grant cross-game/org access and are
 * assigned by an owner, never self-requested.
 *
 * Values MUST match seeded roleIds in rbac-config.json — this is a curated SUBSET of the catalogue,
 * not a second source of truth for it, so keep it in step when disciplines are added or removed.
 * It lives here rather than beside the request modal because the request modal is not the only
 * surface that offers them.
 */
export const REQUESTABLE_GAME_ROLES = [
    { roleId: 'PRODUCER', roleName: 'Producer', description: 'Coordinates development, scheduling and delivery' },
    { roleId: 'PRODUCT_MANAGER', roleName: 'Product Manager', description: 'Owns product strategy, roadmap and priorities' },
    { roleId: 'PROJECT_MANAGER', roleName: 'Project Manager', description: 'Plans and tracks project execution and timelines' },
    { roleId: 'DESIGNER', roleName: 'Designer', description: 'Designs gameplay, systems, levels and content' },
    { roleId: 'DEVELOPER', roleName: 'Developer', description: 'Builds and maintains game features and systems' },
    { roleId: 'DATA_ANALYST', roleName: 'Data Analyst', description: 'Analyzes metrics, funnels and player data' },
    { roleId: 'QUALITY_ANALYST', roleName: 'Quality Analyst', description: 'Tests builds and safeguards release quality' },
    { roleId: 'COMMUNITY_MANAGER', roleName: 'Community Manager', description: 'Manages community, support and player comms' },
    { roleId: 'PUBLISHER', roleName: 'Game Publisher', description: 'Handles publishing, store and release operations' },
];

const byName = (a, b) => String(a.roleName || '').localeCompare(String(b.roleName || ''));

/** Owner before Co-Owner before everything else, so a tier list reads down in authority order. */
const tierRank = (r) => {
    const n = `${r.roleId || ''} ${r.roleName || ''}`.toLowerCase();
    if (n.includes('co-owner') || n.includes('co_owner')) return 1;
    if (n.includes('owner')) return 0;
    return 2;
};

/** "CEO — team role: …" under a "CEO" label is one word of signal and three of echo. */
const stripLeadingName = (text, name) => {
    const n = String(name || '').trim();
    if (!n || !text.toLowerCase().startsWith(n.toLowerCase())) return text;
    const rest = text.slice(n.length).trimStart();
    return /^[—:-]/.test(rest) ? rest.slice(1).trimStart() : text;
};

/**
 * A picker line, not the catalogue entry.
 *
 * Registry descriptions are written for Role Management, where the whole definition belongs —
 * cross-references and all ("co-owner == owner for authorization; see RoleTiers"). Verbatim in a
 * dropdown they cost three lines each and bury the role names they exist to tell apart. Here the job
 * is only to distinguish one role from its neighbours: drop the echo of the role's own name, drop
 * parenthetical asides (always the internal notes), and keep the first clause. The full text stays
 * authoritative where it is authored; nothing is rewritten at the source.
 */
export const summariseRole = (description, label) => {
    let text = String(description || '').replace(/\s*\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim();
    if (!text) return undefined;

    text = stripLeadingName(text, label);

    const cut = text.search(/[.;]/);
    if (cut > 0) text = text.slice(0, cut);
    text = text.trim().replace(/[,:;—-]+$/, '').trim();
    if (text.length > MAX_SUMMARY) text = `${text.slice(0, MAX_SUMMARY - 1).trimEnd()}…`;
    if (!text) return undefined;

    return text.charAt(0).toUpperCase() + text.slice(1);
};

// Every discipline in rbac-config.json carries the SAME sentence — "<name> — team role: full access
// to all product pages; under Game Settings, read-only access to My Requests." True, and identical
// for nine roles, so a picker built from it repeats one line under every option and distinguishes
// nothing. The curated subset above says what each discipline actually does, so it wins where it has
// an entry; everything else (governance tiers, org-custom roles) keeps the registry's own words.
const CURATED_BY_ID = new Map(REQUESTABLE_GAME_ROLES.map(r => [r.roleId, r.description]));

const toOption = (r) => {
    const id = r.roleId || r.id;
    const label = r.roleName || id;
    return { value: id, label, description: summariseRole(CURATED_BY_ID.get(id) || r.description, label) };
};

/**
 * @param {object[]} roles       assignable roles as the registry returns them (roleId, roleName,
 *                               description, system)
 * @param {boolean}  loaded      false while the fetch is in flight — the picker says so rather than
 *                               painting an empty "Select role" that replaces itself a moment later
 * @param {?string}  placeholder leading empty option; pass null to omit it, for a Select that renders
 *                               its own placeholder and does not want a second one in the list
 * @returns {object[]} options for <Select/>
 */
export const buildRoleOptions = ({ roles = [], loaded = true, placeholder = 'Select role' } = {}) => {
    if (!loaded) return [{ value: '', label: 'Loading roles…', isDisabled: true }];

    const valid = (roles || []).filter(r => r && (r.roleId || r.id));
    const tiers = valid.filter(r => r.system).sort((a, b) => tierRank(a) - tierRank(b) || byName(a, b));
    const jobs = valid.filter(r => !r.system).sort(byName);
    // Headers only earn their space when there are two groups to tell apart.
    const showHeaders = tiers.length > 0 && jobs.length > 0;

    const options = placeholder === null ? [] : [{ value: '', label: placeholder }];
    if (showHeaders) options.push({ value: '__hdr_tiers', label: 'Access tiers', isDisabled: true, className: GROUP_CLASS });
    options.push(...tiers.map(toOption));
    if (showHeaders) options.push({ value: '__hdr_jobs', label: 'Job roles', isDisabled: true, className: GROUP_CLASS });
    options.push(...jobs.map(toOption));
    return options;
};

export default buildRoleOptions;
